import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState, useCallback } from 'react'
import { toast } from 'sonner'
import { BRAND_MINT, formatCentsWithCurrency } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { SkeletonTableRow } from '@/components/ui/SkeletonLoader'
import { useDebounce } from '@/hooks/useDebounce'
import { getAdminOrganizationsApi, type AdminOrganization } from '@/lib/api'
import {
  getAdminInvoicesApi,
  generateInvoicesApi,
  type AdminInvoice,
} from '@/lib/billing'
import {
  LoaderIcon,
  FileTextIcon,
  BuildingIcon,
  CreditCardIcon,
  SearchIcon,
  XIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EyeIcon,
  CheckCircleIcon,
  ClockIcon,
  AlertTriangleIcon,
  XCircleIcon,
  RefreshCwIcon,
  FilterIcon,
  CalendarIcon,
  ShieldCheckIcon,
  TrendingUpIcon,
  PlusCircleIcon,
} from 'lucide-react'

const INVOICE_STATUS_STYLES: Record<string, { label: string; color: string }> = {
  pending: { label: 'Pending', color: 'bg-amber-500/10 text-amber-600' },
  paid: { label: 'Paid', color: 'bg-emerald-500/10 text-emerald-600' },
  overdue: { label: 'Overdue', color: 'bg-destructive/10 text-destructive' },
  cancelled: { label: 'Cancelled', color: 'bg-muted text-muted-foreground' },
  refunded: { label: 'Refunded', color: 'bg-purple-500/10 text-purple-600' },
}

function OrgTierBadge({ tier }: { tier: 'growth' | 'enterprise' }) {
  const config = {
    growth:   { icon: TrendingUpIcon, label: 'Growth', color: 'bg-blue-500/10 text-blue-600 dark:text-blue-400' },
    enterprise: { icon: ShieldCheckIcon, label: 'Enterprise', color: 'bg-purple-500/10 text-purple-600 dark:text-purple-400' },
  }
  const c = config[tier] || config.growth
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${c.color}`}>
      <c.icon className="size-3" />
      {c.label}
    </span>
  )
}


function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  })
}

export const Route = createFileRoute('/dashboard/admin/org-subscriptions')({
  component: () => {
    const [orgs, setOrgs] = useState<AdminOrganization[]>([])
    const [orgsTotal, setOrgsTotal] = useState(0)
    const [orgsPage, setOrgsPage] = useState(1)
    const [loading, setLoading] = useState(true)
    const [searchInput, setSearchInput] = useState('')
    const [search, setSearch] = useState('')
    const debouncedSearchInput = useDebounce(searchInput, 300)
    const [billingCycleFilter, setBillingCycleFilter] = useState<string>('')

    // Auto-search when debounced value changes
    useEffect(() => {
      setSearch(debouncedSearchInput.trim())
      setOrgsPage(1)
    }, [debouncedSearchInput])

    // Invoices
    const [invoices, setInvoices] = useState<AdminInvoice[]>([])
    const [invoicesTotal, setInvoicesTotal] = useState(0)
    const [invoicesPage, setInvoicesPage] = useState(1)
    const [invoicesLoading, setInvoicesLoading] = useState(false)
    const [showInvoices, setShowInvoices] = useState(false)

    // Generate invoices
    const [generating, setGenerating] = useState(false)

    const limit = 50

    const fetchOrgs = useCallback(async () => {
      setLoading(true)
      try {
        const params: Record<string, string | number> = { page: orgsPage, limit }
        if (search) params.search = search
        if (billingCycleFilter) params.billing_cycle = billingCycleFilter
        const res = await getAdminOrganizationsApi(params)
        setOrgs(res.data)
        setOrgsTotal(res.total)
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to load organizations')
      } finally {
        setLoading(false)
      }
    }, [orgsPage, search, billingCycleFilter])

    useEffect(() => { fetchOrgs() }, [fetchOrgs, billingCycleFilter])

    const fetchInvoices = useCallback(async (targetPage: number) => {
      setInvoicesLoading(true)
      try {
        const res = await getAdminInvoicesApi({ page: targetPage, limit: 20 })
        setInvoices(res.data)
        setInvoicesTotal(res.total)
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to load invoices')
      } finally {
        setInvoicesLoading(false)
      }
    }, [])

    // Re-fetch invoices when page changes
    useEffect(() => {
      if (showInvoices) {
        fetchInvoices(invoicesPage)
      }
    }, [showInvoices, invoicesPage, fetchInvoices])

    const handleGenerateInvoices = async () => {
      setGenerating(true)
      try {
        const res = await generateInvoicesApi()
        toast.success(res.message || 'Invoice generation completed.')
        fetchInvoices(invoicesPage)
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to generate invoices')
      } finally {
        setGenerating(false)
      }
    }

    const openInvoices = () => {
      setShowInvoices(true)
      setInvoicesPage(1)
    }

    const handleSearch = () => { setSearch(searchInput.trim()); setOrgsPage(1) }

    const orgsTotalPages = Math.max(1, Math.ceil(orgsTotal / limit))
    const invoicesTotalPages = Math.max(1, Math.ceil(invoicesTotal / 20))

    if (loading && orgs.length === 0) {
      return (
        <div className="mx-auto max-w-5xl">
          <div className="mb-8">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <span aria-hidden="true" className="mt-0.5 shrink-0" style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }} />
                <div>
                  <h1 className="text-2xl font-semibold">Billing overview</h1>
                  <p className="mt-1 text-sm text-muted-foreground">Organizations, invoices, and subscription management.</p>
                </div>
              </div>
              <div className="flex gap-2">
                <a href="/dashboard/admin/org-add-ons" className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
                  <PlusCircleIcon className="size-4" />Add-ons
                </a>
                <Button variant="outline" size="sm" onClick={openInvoices} className="gap-1.5">
                  <FileTextIcon className="size-4" />Invoices
                </Button>
                <Button variant="outline" size="sm" onClick={handleGenerateInvoices} disabled={generating} className="gap-1.5">
                  {generating ? <LoaderIcon className="size-4 animate-spin" /> : <RefreshCwIcon className="size-4" />}
                  Generate
                </Button>
              </div>
            </div>
          </div>
          <SkeletonTableRow columns={8} count={6} />
        </div>
      )
    }

    return (
      <div className="mx-auto max-w-5xl">
        <div className="mb-8">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <span aria-hidden="true" className="mt-0.5 shrink-0" style={{ width: '2px', height: '28px', backgroundColor: BRAND_MINT }} />
              <div>
                <h1 className="text-2xl font-semibold">Billing overview</h1>
                <p className="mt-1 text-sm text-muted-foreground">Organizations, invoices, and subscription management.</p>
              </div>
            </div>
            <div className="flex gap-2">
              <a href="/dashboard/admin/org-add-ons" className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
                <PlusCircleIcon className="size-4" />Add-ons
              </a>
              <Button variant="outline" size="sm" onClick={openInvoices} className="gap-1.5">
                <FileTextIcon className="size-4" />Invoices
              </Button>
              <Button variant="outline" size="sm" onClick={handleGenerateInvoices} disabled={generating} className="gap-1.5">
                {generating ? <LoaderIcon className="size-4 animate-spin" /> : <RefreshCwIcon className="size-4" />}
                Generate
              </Button>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="mb-6 flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <input type="text" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              autoFocus
              placeholder="Search organizations…" className="w-full rounded-lg border border-border bg-background py-2 pl-10 pr-3 text-sm outline-none focus:border-primary" />
          </div>
          {/* Billing cycle filter */}
          <select
            value={billingCycleFilter}
            onChange={(e) => { setBillingCycleFilter(e.target.value); setOrgsPage(1) }}
            className="rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary text-foreground"
          >
            <option value="">All billing</option>
            <option value="monthly">Monthly</option>
            <option value="annual">Annual</option>
          </select>
          <Button size="sm" onClick={handleSearch} className="gap-1.5"><SearchIcon className="size-4" />Search</Button>
          {(search || billingCycleFilter) && <Button variant="outline" size="sm" onClick={() => { setSearch(''); setSearchInput(''); setBillingCycleFilter(''); setOrgsPage(1) }} className="gap-1.5"><XIcon className="size-4" />Clear</Button>}
        </div>

        <p className="mb-4 text-xs text-muted-foreground">
          {orgsTotal} organization{orgsTotal !== 1 ? 's' : ''}
        </p>

        {/* Orgs table */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">Organization</th>
                <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">Plan</th>
                <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">Billing</th>
                <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">Status</th>
                <th className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">Country</th>
                <th className="px-4 py-3 text-right text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">Employees</th>
                <th className="px-4 py-3 text-right text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">Created</th>
                <th className="px-4 py-3 w-10" />
              </tr>
            </thead>
            <tbody>
              {orgs.map((org) => (
                <tr key={org.org_id} className="border-b border-border/50 last:border-0 hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <BuildingIcon className="size-4 shrink-0 text-muted-foreground" />
                      <span className="font-medium text-foreground">{org.org_name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {org.tier ? (
                      <OrgTierBadge tier={org.tier} />
                    ) : (
                      <span className="text-[10px] text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {org.billing_cycle === 'annual' ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-400">
                        <ClockIcon className="size-3" />
                        Annual
                      </span>
                    ) : org.billing_cycle === 'monthly' ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-blue-500/10 px-2 py-0.5 text-[10px] font-medium text-blue-600 dark:text-blue-400">
                        <RefreshCwIcon className="size-3" />
                        Monthly
                      </span>
                    ) : (
                      <span className="text-[10px] text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${
                      org.verified_status === 'active' ? 'bg-emerald-500/10 text-emerald-600' :
                      org.verified_status === 'pending' ? 'bg-amber-500/10 text-amber-600' :
                      org.verified_status === 'rejected' ? 'bg-destructive/10 text-destructive' :
                      'bg-muted text-muted-foreground'
                    }`}>
                      {org.verified_status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{org.country_name || '—'}</td>
                  <td className="px-4 py-3 text-right text-xs tabular-nums">{org.employee_count}</td>
                  <td className="px-4 py-3 text-right text-xs text-muted-foreground">{formatDate(org.created_at)}</td>
                  <td className="px-4 py-3">
                    <a href={`/dashboard/admin/organizations`} className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                      <EyeIcon className="size-3.5" />
                    </a>
                  </td>
                </tr>
              ))}                  {orgs.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-sm text-muted-foreground">No organizations found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {orgsTotalPages > 1 && (
          <div className="mt-4 flex items-center justify-center gap-3">
            <Button variant="outline" size="sm" disabled={orgsPage <= 1} onClick={() => setOrgsPage((p) => Math.max(1, p - 1))} className="gap-1.5">
              <ChevronLeftIcon className="size-4" />Previous
            </Button>
            <span className="text-xs text-muted-foreground">Page {orgsPage} of {orgsTotalPages}</span>
            <Button variant="outline" size="sm" disabled={orgsPage >= orgsTotalPages} onClick={() => setOrgsPage((p) => Math.min(orgsTotalPages, p + 1))} className="gap-1.5">
              Next<ChevronRightIcon className="size-4" />
            </Button>
          </div>
        )}

        {/* Invoices Drawer */}
        {showInvoices && (
          <>
            <button type="button" aria-label="Close" className="fixed inset-0 z-40 bg-black/30" onClick={() => setShowInvoices(false)} />
            <aside aria-label="Invoices" className="fixed inset-y-0 right-0 z-50 w-full max-w-2xl border-l border-border bg-background shadow-xl motion-safe:animate-in motion-safe:slide-in-from-right overflow-y-auto">
              <div className="flex items-center justify-between border-b border-border px-5 py-4">
                <h2 className="text-base font-semibold">All invoices</h2>
                <button type="button" onClick={() => setShowInvoices(false)} className="flex size-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground">
                  <XIcon className="size-4" />
                </button>
              </div>
              <div className="p-5">
                {invoicesLoading ? (
                  <div className="flex items-center justify-center py-16"><LoaderIcon className="size-5 animate-spin text-muted-foreground" /></div>
                ) : invoices.length === 0 ? (
                  <p className="py-16 text-center text-sm text-muted-foreground">No invoices yet.</p>
                ) : (
                  <>
                    <div className="space-y-2">
                      {invoices.map((inv) => {
                        const s = INVOICE_STATUS_STYLES[inv.status] ?? { label: inv.status, color: 'bg-muted text-muted-foreground' }
                        return (
                          <div key={inv.invoice_id} className="flex items-center gap-3 rounded-lg border border-border bg-card p-3">
                            <FileTextIcon className="size-4 shrink-0 text-muted-foreground" />
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-foreground">{inv.invoice_number}</span>
                                <span className={`inline-flex shrink-0 items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium ${s.color}`}>{s.label}</span>
                              </div>
                              <p className="text-xs text-muted-foreground">{inv.org_name} · {formatDate(inv.period_start)} — {formatDate(inv.period_end)}</p>
                            </div>
                            <span className="text-sm font-semibold tabular-nums">{formatCentsWithCurrency(inv.total_cents, inv.currency)}</span>
                          </div>
                        )
                      })}
                    </div>
                    {invoicesTotalPages > 1 && (
                      <div className="mt-4 flex items-center justify-center gap-3">
                        <Button variant="outline" size="sm" disabled={invoicesPage <= 1} onClick={() => setInvoicesPage((p) => Math.max(1, p - 1))}>
                          <ChevronLeftIcon className="size-4" />
                        </Button>
                        <span className="text-xs text-muted-foreground">{invoicesPage} / {invoicesTotalPages}</span>
                        <Button variant="outline" size="sm" disabled={invoicesPage >= invoicesTotalPages} onClick={() => setInvoicesPage((p) => Math.min(invoicesTotalPages, p + 1))}>
                          <ChevronRightIcon className="size-4" />
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </aside>
          </>
        )}
      </div>
    )
  },
})
